import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail, formatCNPJ, docLabel } from "../lib/api";
import { safeReturnPath } from "../lib/stepUp";
import { maskAgenciaBR, maskContaBR, finalizeBankIdentBR } from "../lib/numberFormat";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Shield, ShieldCheck, ShieldOff, Smartphone, Copy, Check, Landmark, Clock, X, Moon } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { BankSelect } from "../components/BankSelect";
import { bankByCode } from "../lib/banks";
import { Switch } from "../components/ui/switch";
import { useTheme } from "../lib/theme";
import { toast } from "sonner";
import CompanyDocuments, { ConfigTabs, PendenciaDocumentos } from "../components/CompanyDocuments";

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const [theme, setTheme] = useTheme();
  // FS-035 — deep-link de remediação: quem foi cortado na tela de Tokens API
  // chega aqui com `?retorno=/integracao/tokens` e, ao ATIVAR o 2FA, volta
  // sozinho de onde veio. `safeReturnPath` só aceita path INTERNO conhecido
  // (allowlist em lib/stepUp): o valor vem da query string, e um destino
  // aberto viraria redirect controlado por quem monta a URL.
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const retorno = safeReturnPath(searchParams.get("retorno"));
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [copied, setCopied] = useState(false);
  // Bank account state
  const [bankChangeOpen, setBankChangeOpen] = useState(false);
  const [bankRequest, setBankRequest] = useState(null);
  const [bankForm, setBankForm] = useState({ banco: "", agencia: "", conta: "", tipo: "corrente" });
  // docs-cadastro (DOC13): aba Documentos dentro de Configurações do Parceiro.
  const [aba, setAba] = useState("geral");

  const startSetup = async () => {
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("2FA ativado com sucesso!");
      setSetup(null);
      setCode("");
      // `refreshMe` ANTES de navegar: a tela de destino decide pelo
      // `two_factor_enabled` do contexto — voltar com o dado velho mostraria o
      // banner de corte para quem acabou de ativar.
      await refreshMe();
      if (retorno) navigate(retorno);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      toast.success("2FA desativado");
      setDisableCode("");
      await refreshMe();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = () => {
    if (!setup?.secret) return;
    navigator.clipboard.writeText(setup.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <span className="zemo-overline">Conta</span>
        <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight">Perfil &amp; Segurança</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Informações da sua conta e configuração da autenticação em 2 etapas.
        </p>
      </div>

      <ConfigTabs aba={aba} onChange={setAba} />
      {aba === "documentos" && (
        <CompanyDocuments doc={user?.partner_cnpj} canUpload={["admin", "operator"].includes(user?.role)} />
      )}
      {aba !== "documentos" && (<>
      {/* Owner C1009756/C1009769: o mesmo aviso de pendência também na aba inicial. */}
      <PendenciaDocumentos doc={user?.partner_cnpj} />
      {/* Identity card */}
      <div className="zemo-card p-6">
        <span className="zemo-overline">Identidade</span>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <Field label="Nome" value={user?.name} />
          <Field label="E-mail" value={user?.email} />
          <Field label="Permissão" value={user?.role} />
          <Field label="Parceiro" value={user?.partner_name} />
          {/* Documento cadastrado do parceiro — informativo, read-only. É o
              MESMO documento do BO e da V2 (fonte única: o cadastro). */}
          <Field
            label={docLabel(user?.partner_cnpj)}
            value={formatCNPJ(user?.partner_cnpj)}
            mono
            testid="profile-partner-cnpj"
          />
        </div>
      </div>

      {/* 2FA */}
      <div className="zemo-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className={`h-11 w-11 rounded-md flex items-center justify-center ${user?.two_factor_enabled ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-700"}`}>
              {user?.two_factor_enabled ? <ShieldCheck className="h-5 w-5" /> : <Shield className="h-5 w-5" />}
            </div>
            <div>
              <h3 className="text-lg font-semibold tracking-tight">Autenticação em 2 etapas</h3>
              <p className="text-[13px] text-zinc-500 mt-0.5 max-w-md">
                Reforce sua conta com um código gerado por um app autenticador (Google Authenticator, 1Password, Authy).
              </p>
            </div>
          </div>
          {user?.two_factor_enabled ? (
            <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="2fa-status-active">
              <ShieldCheck className="h-3 w-3 mr-1" /> Ativo
            </Badge>
          ) : (
            <Badge variant="outline" className="text-zinc-600" data-testid="2fa-status-inactive">
              <ShieldOff className="h-3 w-3 mr-1" /> Desativado
            </Badge>
          )}
        </div>

        {!user?.two_factor_enabled && !setup && (
          <div className="mt-5">
            <Button
              onClick={startSetup}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              data-testid="2fa-setup-start"
            >
              <Smartphone className="h-4 w-4 mr-1.5" /> Configurar 2FA agora
            </Button>
          </div>
        )}

        {setup && (
          <form onSubmit={enable} className="mt-6 space-y-5" data-testid="2fa-setup-form">
            <div className="grid sm:grid-cols-[180px_1fr] gap-5 items-start">
              <div className="rounded-md border border-[var(--zemo-border)] p-3 bg-white">
                <img
                  src={`data:image/png;base64,${setup.qr_png_base64}`}
                  alt="QR code 2FA"
                  className="w-full h-auto"
                />
              </div>
              <div className="space-y-3 text-sm">
                <p className="font-semibold text-zinc-900">1. Escaneie o QR no seu app autenticador</p>
                <p className="text-zinc-500 text-[13px]">
                  Ou insira manualmente o código abaixo:
                </p>
                <div className="flex items-center gap-2 p-2 rounded-md border border-[var(--zemo-border)] bg-zinc-50">
                  <code className="flex-1 font-mono-num text-[12px] tracking-wider break-all">{setup.secret}</code>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={copySecret}
                    data-testid="2fa-copy-secret"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
                <p className="font-semibold text-zinc-900 pt-2">2. Digite o código gerado pelo app</p>
                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="2fa-enable-code">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-11 w-11" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    type="submit"
                    disabled={submitting || code.length !== 6}
                    className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                    data-testid="2fa-confirm-enable"
                  >
                    Ativar 2FA
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => { setSetup(null); setCode(""); }}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}

        {user?.two_factor_enabled && (
          <form onSubmit={disable} className="mt-6 max-w-md space-y-3 border-t border-[var(--zemo-border)] pt-5" data-testid="2fa-disable-form">
            <Label className="text-[13px] font-medium">Desativar 2FA</Label>
            <p className="text-[12px] text-zinc-500 -mt-1">
              Para desativar, confirme com um código atual do app autenticador.
            </p>
            <div className="flex items-center gap-3">
              <InputOTP maxLength={6} value={disableCode} onChange={setDisableCode}>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="h-10 w-10" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              <Button
                type="submit"
                variant="outline"
                disabled={submitting || disableCode.length !== 6}
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                data-testid="2fa-confirm-disable"
              >
                Desativar
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Appearance */}
      <div className="zemo-card p-6" data-testid="appearance-section">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-zinc-100 border border-[var(--zemo-border)] flex items-center justify-center text-zinc-700">
              <Moon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold tracking-tight">Aparência</h3>
              <p className="text-[13px] text-zinc-500 mt-0.5">Tema escuro reduz o brilho da interface. Preferência salva neste dispositivo.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-zinc-500">Tema escuro</span>
            <Switch
              checked={theme === "dark"}
              onCheckedChange={(v) => setTheme(v ? "dark" : "light")}
              data-testid="theme-switch"
            />
          </div>
        </div>
      </div>

      {/* Bank Account */}
      <div className="zemo-card p-6 space-y-5" data-testid="partner-bank-section">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-700">
            <Landmark className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold tracking-tight">Dados Bancários</h3>
            <p className="text-[13px] text-zinc-500 mt-0.5">Conta para recebimento de comissões.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {[["Banco", "Banco Zemo"], ["Agência", "0001"], ["Conta", "54321-0"], ["Tipo", "Corrente"]].map(([k, v]) => (
            <div key={k} className="p-3 border border-[var(--zemo-border)] rounded-md bg-zinc-50/30">
              <div className="zemo-overline">{k}</div>
              <div className="mt-1 font-semibold text-zinc-900 text-sm font-mono-num">{v}</div>
            </div>
          ))}
        </div>
        {bankRequest && (
          <div className="border border-amber-200 rounded-md bg-amber-50 p-4 space-y-3">
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-amber-600" /><span className="text-sm font-semibold text-amber-900">Alteração pendente</span></div>
            {/* FS-247: o BankSelect agora emite o CÓDIGO — este bloco exibia o
                valor cru do form, então passaria a mostrar "237" no lugar do
                nome do banco. Resolve o rótulo pelo código (fallback = o
                próprio valor, para não sumir com dado desconhecido). */}
            <div className="text-sm text-zinc-900 font-semibold">{bankByCode(bankRequest.banco)?.label || bankRequest.banco} · Ag {bankRequest.agencia} · {bankRequest.tipo === "corrente" ? "CC" : "CP"} {bankRequest.conta}</div>
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setBankRequest(null); toast.info("Solicitação cancelada"); }}>
              <X className="h-3.5 w-3.5 mr-1" /> Cancelar
            </Button>
          </div>
        )}
        {!bankRequest && !bankChangeOpen && user?.role === "admin" && (
          <Button variant="outline" onClick={() => setBankChangeOpen(true)}>Solicitar alteração de conta</Button>
        )}
        {bankChangeOpen && (
          <form onSubmit={(e) => { e.preventDefault(); setBankRequest({...bankForm}); setBankChangeOpen(false); toast.success("Solicitação enviada para aprovação"); }} className="border border-[var(--zemo-border)] rounded-md p-4 space-y-4 bg-white">
            <h4 className="text-sm font-semibold">Nova conta para comissões</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Banco</Label><BankSelect value={bankForm.banco} onChange={(v) => setBankForm({...bankForm, banco: v})} /></div>
              <div className="space-y-1.5"><Label>Tipo</Label><select className="w-full h-10 rounded-md border border-input px-3 text-sm" value={bankForm.tipo} onChange={(e) => setBankForm({...bankForm, tipo: e.target.value})}><option value="corrente">Conta Corrente</option><option value="poupanca">Conta Poupança</option></select></div>
              <div className="space-y-1.5"><Label>Agência</Label><Input required inputMode="text" value={bankForm.agencia} onChange={(e) => setBankForm({...bankForm, agencia: maskAgenciaBR(e.target.value, bankForm.agencia)})} onBlur={(e) => setBankForm({...bankForm, agencia: finalizeBankIdentBR(e.target.value)})} placeholder="0001-2" className="font-mono-num" /></div>
              <div className="space-y-1.5"><Label>Conta</Label><Input required inputMode="text" value={bankForm.conta} onChange={(e) => setBankForm({...bankForm, conta: maskContaBR(e.target.value, bankForm.conta)})} onBlur={(e) => setBankForm({...bankForm, conta: finalizeBankIdentBR(e.target.value)})} placeholder="12345-6" className="font-mono-num" /></div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => setBankChangeOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] text-white" disabled={!bankForm.banco}>Enviar para aprovação</Button>
            </div>
          </form>
        )}
      </div>
      </>)}
    </div>
  );
}

// `mono`: documento/número — fonte tabular e SEM `capitalize` (que não afeta
// dígitos, mas sinaliza texto). Default segue idêntico ao anterior.
function Field({ label, value, mono = false, testid }) {
  return (
    <div className="p-3 border border-[var(--zemo-border)] rounded-md bg-zinc-50/30">
      <div className="zemo-overline">{label}</div>
      <div
        className={`mt-1 font-semibold text-zinc-900 text-sm ${mono ? "font-mono-num" : "capitalize"}`}
        data-testid={testid}
      >
        {value || "—"}
      </div>
    </div>
  );
}
